import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download } from "lucide-react";
import { Helmet } from "react-helmet-async";
import timetableWhitepaperCover from "@/assets/timetable-whitepaper-full.png.asset.json";

const WhitePaperTimetableIntro = () => {
  return (
    <>
      <Helmet>
        <title>Timetable Optimization White Paper: From Schedules to Efficiency | Cetasol</title>
        <meta
          name="description"
          content="Timetable optimization white paper: how operational data, vessel-specific modelling and simulation reveal the efficiency potential in your vessel timetable."
        />
        <link rel="canonical" href="https://cetasol.com/timetable-optimization-white-paper" />
        <meta property="og:title" content="Timetable Optimization White Paper: From Schedules to Efficiency | Cetasol" />
        <meta property="og:description" content="Timetable optimization white paper: how operational data, vessel-specific modelling and simulation reveal the efficiency potential in your vessel timetable." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://cetasol.com/timetable-optimization-white-paper" />
        <meta property="og:image:alt" content="Timetable optimization white paper cover" />
        <meta property="og:image" content={`https://cetasol.com${timetableWhitepaperCover.url}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Timetable Optimization White Paper: From Schedules to Efficiency | Cetasol" />
        <meta name="twitter:description" content="Timetable optimization white paper: how operational data, vessel-specific modelling and simulation reveal the efficiency potential in your vessel timetable." />
        <meta name="twitter:image" content={`https://cetasol.com${timetableWhitepaperCover.url}`} />
      </Helmet>
      <Header />
      <main className="min-h-screen pt-24 pb-16">
        <article className="container mx-auto px-6 max-w-3xl">
          <Link
            to="/whitepapers"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft size={16} /> Back to all white papers
          </Link>

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              From schedules to efficiency: The hidden potential in your vessel timetable
            </h1>
            <Link to="/whitepaper/timetable-optimization-download" className="inline-block mt-6">
              <Button className="gap-2">
                <Download size={16} /> Download PDF
              </Button>
            </Link>
          </header>

          <img
            src={timetableWhitepaperCover.url}
            alt="Timetable optimization White Paper cover"
            className="w-full rounded-lg mb-8"
          />

          <div className="prose prose-invert prose-lg max-w-none prose-p:text-foreground/80 prose-p:leading-relaxed prose-p:mb-4">
            <p>
              Timetables do more than determine when a vessel arrives. They influence how the vessel needs to operate, and therefore its energy consumption, efficiency and operational performance.
            </p>
            <p>
              Timetable optimization provides a practical way to identify improvement opportunities by understanding how individual vessels actually perform and exploring alternative schedules before they are implemented.
            </p>
            <p>
              By combining operational data, vessel-specific modelling and simulation, operators can evaluate different timetable scenarios, validate their feasibility and understand their potential impact before making changes in real operations.
            </p>
            <p>
              The goal isn't simply a faster or slower timetable. It's a smarter one, balancing efficiency with the service the operation needs to deliver. Download the full white paper to see how.
            </p>
          </div>

          <div className="mt-10 flex justify-center">
            <Link to="/whitepaper/timetable-optimization-download">
              <Button className="gap-2">
                <Download size={16} /> Download PDF
              </Button>
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
};

export default WhitePaperTimetableIntro;
